// LiveBroadcast.tsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';

type BroadcastState = 'idle' | 'starting' | 'live' | 'error';

export interface LiveBroadcastProps {
  /** Your IVS channel’s stream key */
  streamKey: string;
  /** Ingest endpoint, e.g. UNIQUE_ID.global-contribute.live-video.net */
  ingestEndpoint: string;
  /** Optional preset (BASIC_LANDSCAPE, STANDARD_LANDSCAPE, etc.) */
  streamConfig?: any;
  /** Receives broadcast state updates and errors */
  onBroadcastStateChange?: (state: BroadcastState, info?: any) => void;
}

const LiveBroadcast: React.FC<LiveBroadcastProps> = ({
  streamKey,
  ingestEndpoint,
  streamConfig = window.IVSBroadcastClient.BASIC_LANDSCAPE,
  onBroadcastStateChange = () => { },
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const clientRef = useRef<any>(null);
  const videoStreamRef = useRef<MediaStream | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);

  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<string>('');
  const [selectedAudio, setSelectedAudio] = useState<string>('');
  const [broadcastState, setBroadcastState] = useState<BroadcastState>('idle');
  const [loading, setLoading] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isVideoStopped, setIsVideoStopped] = useState<boolean>(false);

  // Initialize IVS client and enumerate devices
  useEffect(() => {
    if (!window.IVSBroadcastClient) {
      console.error('IVS Broadcast SDK not loaded');
      return;
    }

    const client = window.IVSBroadcastClient.create({
      ingestEndpoint,
      streamConfig,
    });
    clientRef.current = client;

    // Attach preview to canvas on mount
    if (canvasRef.current) {
      client.attachPreview(canvasRef.current);
    }

    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then((stream) => {
        stream.getTracks().forEach((t) => t.stop());
        return navigator.mediaDevices.enumerateDevices();
      })
      .then((devices) => {
        setVideoDevices(devices.filter((d) => d.kind === 'videoinput'));
        setAudioDevices(devices.filter((d) => d.kind === 'audioinput'));
      })
      .catch((err) => {
        console.error('Media permissions error', err);
        alert('Camera and microphone permissions are required to broadcast.');
        // setBroadcastState('error');
        onBroadcastStateChange('error', err);
      });



    return () => {
      client.stopBroadcast?.();
      clientRef.current = null;
    };
  }, [ingestEndpoint, streamConfig]);

  // Handlers for selecting devices
  const handleVideoChange = async (evt: SelectChangeEvent<string>) => {
    const deviceId = evt.target.value;
    setSelectedVideo(deviceId);

    const camStream = await navigator.mediaDevices.getUserMedia({
      video: {
        deviceId,
        width: { ideal: streamConfig.maxResolution?.width },
        height: { ideal: streamConfig.maxResolution?.height },
      },
    });

    videoStreamRef.current?.getTracks().forEach((t) => t.stop());
    videoStreamRef.current = camStream;

    try {
      clientRef.current.removeVideoInputDevice('camera');
    } catch { }
    clientRef.current.addVideoInputDevice(camStream, 'camera', { index: 0 });
  };

  const handleAudioChange = async (evt: SelectChangeEvent<string>) => {
    const deviceId = evt.target.value;
    setSelectedAudio(deviceId);

    const micStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId,
        noiseSuppression: false,
      },
    });

    audioStreamRef.current?.getTracks().forEach((t) => t.stop());
    audioStreamRef.current = micStream;

    try {
      clientRef.current.removeAudioInputDevice('microphone');
    } catch { }
    clientRef.current.addAudioInputDevice(micStream, 'microphone');
  };

  // Start / Stop broadcast
  const startBroadcast = async () => {
    setLoading(true);
    setBroadcastState('starting');
    onBroadcastStateChange('starting');
    try {
      await clientRef.current.startBroadcast(streamKey);
      const sessionId: string | undefined = clientRef.current.getSessionId();
      console.log('Live session ID:', sessionId);
      setBroadcastState('live');
      onBroadcastStateChange('live', { sessionId });
    } catch (err) {
      console.error(err);
      setBroadcastState('error');
      onBroadcastStateChange('error', err);
    } finally {
      setLoading(false);
    }
  };

  const stopBroadcast = () => {
    if (!window.confirm('Are you sure you want to end the stream?')) return;
    clientRef.current.stopBroadcast();
    try { clientRef.current.removeVideoInputDevice('camera'); } catch { }
    try { clientRef.current.removeAudioInputDevice('microphone'); } catch { }
    videoStreamRef.current?.getTracks().forEach((t) => t.stop());
    audioStreamRef.current?.getTracks().forEach((t) => t.stop());
    videoStreamRef.current = null;
    audioStreamRef.current = null;
    setBroadcastState('idle');
    onBroadcastStateChange('idle');
    setIsMuted(false);
    setIsVideoStopped(false);
  };

  // Mute / Video toggles
  const toggleMute = () => {
    if (!audioStreamRef.current) return;
    audioStreamRef.current.getAudioTracks().forEach((t) => {
      t.enabled = isMuted; // enable if currently muted
    });
    setIsMuted((m) => !m);
  };

  const toggleVideo = () => {
    if (!videoStreamRef.current) return;
    videoStreamRef.current.getVideoTracks().forEach((t) => {
      t.enabled = isVideoStopped; // enable if currently stopped
    });
    setIsVideoStopped((v) => !v);
  };

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {/* Preview container with overlay placeholder */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          pt: '56.25%', // 16:9 aspect ratio
          border: '1px solid #ccc',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            transform: 'scaleX(-1)', // invert preview
          }}
        />
        {!selectedVideo && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.8)',
            }}
          >
            <CameraAltIcon fontSize="large" color="disabled" />
            <Typography color="textSecondary" mt={1}>
              Preview will appear here once you select a camera
            </Typography>
          </Box>
        )}
      </Box>

      {/* Device selectors */}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="video-select-label">Camera</InputLabel>
            <Select
              labelId="video-select-label"
              value={selectedVideo}
              label="Camera"
              onChange={handleVideoChange}
            >
              {videoDevices.map((d) => (
                <MenuItem key={d.deviceId} value={d.deviceId}>
                  {d.label || `Camera ${d.deviceId}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="audio-select-label">Microphone</InputLabel>
            <Select
              labelId="audio-select-label"
              value={selectedAudio}
              label="Microphone"
              onChange={handleAudioChange}
            >
              {audioDevices.map((d) => (
                <MenuItem key={d.deviceId} value={d.deviceId}>
                  {d.label || `Microphone ${d.deviceId}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Icon buttons for mute & video */}
      {broadcastState === 'live' && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton
            onClick={toggleMute}
            color={isMuted ? 'error' : 'primary'}
            aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
          >
            {isMuted ? <MicOffIcon /> : <MicIcon />}
          </IconButton>
          <IconButton
            onClick={toggleVideo}
            color={isVideoStopped ? 'error' : 'primary'}
            aria-label={isVideoStopped ? 'Start video' : 'Stop video'}
          >
            {isVideoStopped ? <VideocamOffIcon /> : <VideocamIcon />}
          </IconButton>
        </Box>
      )}

      {/* Broadcast controls */}
      {broadcastState !== 'live' ? (
        <Button
          variant="contained"
          onClick={startBroadcast}
          disabled={!selectedVideo || !selectedAudio || loading}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? 'Starting...' : 'Start Broadcast'}
        </Button>
      ) : (
        <Button variant="outlined" color="error" onClick={stopBroadcast}>
          Stop Broadcast
        </Button>
      )}
    </Box>
  );
};

export default LiveBroadcast;
